import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { DCheckbox, DPage, DPageLoader, DYear } from '../../../dum';
import {
    SalesVsExpensesHistoricExpensesFragmentFragment,
    SalesVsExpensesHistoricSalesFragmentFragment,
    useSalesVsExpensesHistoricPageLazyQuery,
} from '../../../../services';
import dayjs from 'dayjs';
import { DataSet, LineChart } from '../../../dum/simple/charts';

function createMonthsArray<S>(callback: (m: string, index: number) => [string, S]): [string, S][] {
    return dayjs.months().map((month, index) => {
        return callback(month.slice(0, 3), index);
    });
}


type MonthDataSet = DataSet<[string, number]>

function createMonthDataSet({color, name, year, SalesVsExpensesHistoric}: {
    name: string; year: number | null, color:string, SalesVsExpensesHistoric: SalesVsExpensesHistoricSalesFragmentFragment | SalesVsExpensesHistoricExpensesFragmentFragment

}): MonthDataSet {
    return {
        name: name,
        data: createMonthsArray((month, index) => {
            if (SalesVsExpensesHistoric.__typename === 'SalesSummary' ) {
                const foundSale = SalesVsExpensesHistoric.sales.find(sale => {
                    return sale.month === index + 1
                })
                return [`${month} ${year}`, foundSale?.total_with_tax || 0]
            }

            if (SalesVsExpensesHistoric.__typename === 'ExpensesSummary' ) {
                const foundExpense = SalesVsExpensesHistoric.expenses.find(expense => {
                    return expense.month === index + 1
                })
                return [`${month} ${year}`, foundExpense?.total_with_tax || 0]
            }

            return [`${month} ${year}`, 0]

        }),
        color: color
    }

}


export function SalesVsExpensesHistoricExpenses() {
    const [query, { data, loading }] = useSalesVsExpensesHistoricPageLazyQuery();
    const [year, setYear] = useState<number | null>(dayjs().year());
    const [includeReconciliationOnly, setIncludeReconciliationOnly] =
        useState(false);

    const dataSets: MonthDataSet[] = useMemo(() => {
        if (!data || !data.getSalesTotalSummaryYear || !year) {
            return []
        }

        const sales2025 = createMonthDataSet({
            color: "#2196f3",
            name: `Ventas ${year - 1} - ${year}`,
            year: year,
            SalesVsExpensesHistoric: data.getSalesTotalSummaryYear
        })
        const sales2024 = createMonthDataSet({
            color: "#2196f3",
            name: `Ventas ${year - 1} - ${year}`,
            year: year - 1,
            SalesVsExpensesHistoric: data.getSalesTotalSummaryYearMinusOne
        })

        const expenses2024 = createMonthDataSet({
            color: "#b71c1c",
            name: `Gatos ${year - 1} - ${year}`,
            year: year - 1,
            SalesVsExpensesHistoric: data.getExpensesTotalSummaryYearMinusOne
        })
        const expenses2025 = createMonthDataSet({
            color: "#b71c1c",
            name: `Gastos ${year}`,
            year: year,
            SalesVsExpensesHistoric: data.getExpensesTotalSummaryYear
        })


        sales2024.data = [...sales2024.data, ...sales2025.data]
        expenses2024.data = [...expenses2024.data, ...expenses2025.data]

        return [
            sales2024,
            expenses2024,
        ]
    }, [data, year])


    useEffect(() => {
        if (year) {
            void query({
                variables: {
                    year: year,
                    yearMinusOne: year - 1,
                    month: null,
                    includeReconciliationOnly,
                },
            });
        }
    }, [query, year, includeReconciliationOnly]);




    return (
        <DPage>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <DYear setYear={setYear} year={year} />
                <DCheckbox
                    value={includeReconciliationOnly}
                    label={'Incluir solo conciliación'}
                    onChange={setIncludeReconciliationOnly}
                    disableGutters
                />
            </Box>
            {loading && !data ? (
                <DPageLoader />
            ) : (
                <LineChart
                    getX={
                        (item) => {
                            return item.length > 0 ? item[0] : ""
                        }
                    }
                    getY={
                        (item) => {
                            return item.length > 1 ? item[1] : 0
                        }
                    }
                    dataSets={dataSets}
                />
            )}
        </DPage>
    );
}
